// SEGUIMIENTO DEL CASO (modal de la bandeja; operador, 5-oct-2026, vista previa aprobada).
// Spec: `05_analisis_tecnico/Farmacovigilancia/ui/MG-ILE-67.md`, «Modal de seguimiento del caso».
//
// Lee `GET /tableros/seguimiento/{caso_id}` (`api/tableros.ts`, `consultarSeguimiento`) y pinta
// tres cosas: el FLUJO de las seis etapas, un RESUMEN en tarjetas y la LÍNEA DE TIEMPO de hitos
// agrupada por etapa.
//
// LA PANTALLA NO CALCULA NADA (`estandares_frontend.md` §8.1 R1): el estado de cada etapa, las
// duraciones («2 h 15 min desde el hito anterior»), el plazo y los textos llegan redactados por el
// servidor y se pintan tal cual. Aquí solo se formatean fechas (`lib/fechas.ts`) y se agrupan los
// hitos en el orden en que llegan.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Check, History, RefreshCw } from 'lucide-react';
import { Alert, Button, Modal } from './ui';
import { consultarSeguimiento } from '../api/tableros';
import type {
  EtapaDelSeguimiento, HitoDelSeguimiento, SeguimientoDelCaso as DatosDelSeguimiento,
} from '../api/tableros';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { comoFechaDeInstante, comoFechaYHora } from '../lib/fechas';
import { useZonaDelPais } from '../lib/CatalogosContexto';

export type PropsDeSeguimientoDelCaso = {
  casoId: string;
  /** El número del caso, para el título mientras carga. */
  numero?: string | null;
  /** El país del caso: las fechas y horas van en su zona (RN-82). */
  pais?: string | null;
  onCerrar: () => void;
};

type EstadoDeEtapa = EtapaDelSeguimiento['estado'];

/** Lo que oye el lector de pantalla junto a cada etapa (el color no basta, WCAG 1.4.1). */
const TEXTO_OCULTO: Record<EstadoDeEtapa, string> = {
  hecha: 'completada',
  actual: 'en curso',
  pendiente: 'pendiente',
};

const CIRCULO: Record<EstadoDeEtapa, string> = {
  hecha: 'bg-marca border-marca text-white',
  actual: 'bg-white border-marca text-marca-profundo ring-4 ring-marca-bruma',
  pendiente: 'bg-white border-borde text-ink-soft',
};

const NOMBRE: Record<EstadoDeEtapa, string> = {
  hecha: 'text-ink',
  actual: 'text-marca-profundo',
  pendiente: 'text-ink',
};

/** La raya hacia la etapa anterior: de marca hasta la actual, gris en adelante. */
const CONECTOR: Record<EstadoDeEtapa, string> = {
  hecha: 'bg-marca',
  actual: 'bg-marca',
  pendiente: 'bg-borde',
};

/** Solo la actual se anuncia como el paso en curso (`aria-current="step"`). */
const PASO_EN_CURSO: Record<EstadoDeEtapa, 'step' | undefined> = {
  hecha: undefined,
  actual: 'step',
  pendiente: undefined,
};

/** Lo que va bajo una etapa que aún no tiene fecha de entrada. */
const SIN_FECHA: Record<EstadoDeEtapa, string> = {
  hecha: '',
  actual: 'En curso',
  pendiente: '',
};

/** El testid lleva el código en kebab-case (`evaluacion_comite` → `evaluacion-comite`, §7). */
const enKebab = (codigo: string) => codigo.split('_').join('-');

/**
 * El conector que une cada etapa con la anterior. La primera no lleva; en móvil (3 columnas) la
 * cuarta abre fila y tampoco, para que la raya no salga del cuadro.
 */
function clasesDelConector(i: number, estado: EstadoDeEtapa): string {
  if (i === 0) return '';
  const visible = i === 3 ? 'hidden sm:block' : 'block';
  return `${visible} ${CONECTOR[estado]}`;
}

function Flujo({ etapas, zona }: { etapas: EtapaDelSeguimiento[]; zona: string | null }) {
  return (
    <ol aria-label="Etapas del caso" className="grid grid-cols-3 sm:grid-cols-6 gap-y-4 pt-2">
      {etapas.map((etapa, i) => {
        const conector = clasesDelConector(i, etapa.estado);
        const fecha = etapa.entro_el ? comoFechaDeInstante(etapa.entro_el, zona) : '';
        return (
          <li key={etapa.codigo} data-testid={`seguimiento-etapa-${enKebab(etapa.codigo)}`} data-estado={etapa.estado}
            aria-current={PASO_EN_CURSO[etapa.estado]}
            className="relative text-center px-1">
            {conector && <span aria-hidden="true" className={`absolute top-[15px] -left-1/2 right-1/2 h-0.5 ${conector}`} />}
            <span aria-hidden="true"
              className={`relative z-[1] mx-auto mb-1.5 grid place-items-center w-8 h-8 rounded-full border-2 text-compacto font-bold tabular-nums ${CIRCULO[etapa.estado]}`}>
              {etapa.estado === 'hecha' ? <Check size={16} strokeWidth={3} /> : i + 1}
            </span>
            <span className={`block text-micro font-semibold ${NOMBRE[etapa.estado]}`}>
              {etapa.texto}
              <span className="sr-only">, {TEXTO_OCULTO[etapa.estado]}</span>
            </span>
            <span className="block text-micro text-ink-soft tabular-nums">
              {fecha || SIN_FECHA[etapa.estado]}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function Dato({ dato, etiqueta, children }: { dato: string; etiqueta: string; children: ReactNode }) {
  return (
    <div data-testid={`seguimiento-resumen-${dato}`} className="border border-hairline rounded-xl px-3 py-2 min-w-[9.5rem] flex-1">
      <dt className="text-micro text-ink-soft">{etiqueta}</dt>
      <dd className="text-body font-semibold text-ink">{children}</dd>
    </div>
  );
}

function Resumen({ seguimiento }: { seguimiento: DatosDelSeguimiento }) {
  const { resumen } = seguimiento;
  return (
    <dl className="flex flex-wrap gap-2.5">
      <Dato dato="etapa" etiqueta="Etapa actual">{seguimiento.texto_de_la_etapa}</Dato>
      <Dato dato="responsable" etiqueta="Responsable">{seguimiento.responsable_actual ?? 'Sin responsable'}</Dato>
      <Dato dato="en-la-etapa" etiqueta="En la etapa">{resumen.texto_en_la_etapa ?? 'No aplica'}</Dato>
      <Dato dato="desde-la-recepcion" etiqueta="Desde la recepción">{resumen.texto_desde_la_recepcion ?? 'No aplica'}</Dato>
      <Dato dato="plazo" etiqueta="Plazo">
        {resumen.texto_corto_del_plazo}
        {resumen.detalle_del_plazo && (
          <span className="block text-meta font-normal text-ink-soft">{resumen.detalle_del_plazo}</span>
        )}
      </Dato>
    </dl>
  );
}

/** Cada hito va con su posición en la lista del servidor: es su clave y dice cuál es el primero. */
type Grupo = { clave: string; texto: string | null; hitos: { hito: HitoDelSeguimiento; indice: number }[] };

/**
 * Los hitos en el orden del servidor, partidos cada vez que cambia la etapa. Una anotación o un
 * aviso (`etapa` nula) se queda en su posición cronológica dentro del grupo en curso, sin abrir
 * cabecera nueva. La cabecera dice el texto de la etapa tal como viene en `etapas[]`.
 */
function agrupar(hitos: HitoDelSeguimiento[], etapas: EtapaDelSeguimiento[]): Grupo[] {
  const textoDe = new Map(etapas.map((e) => [e.codigo, e.texto]));
  const grupos: Grupo[] = [];
  let etapaEnCurso: string | null = null;
  hitos.forEach((hito, i) => {
    const abre = hito.etapa !== null && hito.etapa !== etapaEnCurso;
    if (abre || grupos.length === 0) {
      if (hito.etapa !== null) etapaEnCurso = hito.etapa;
      grupos.push({ clave: `${i}-${hito.etapa ?? ''}`, texto: hito.etapa ? textoDe.get(hito.etapa) ?? null : null, hitos: [] });
    }
    grupos[grupos.length - 1].hitos.push({ hito, indice: i });
  });
  return grupos;
}

const fechaDelHito = (hito: HitoDelSeguimiento, zona: string | null) => (
  hito.solo_fecha ? comoFechaDeInstante(hito.ocurrido_en, zona) : comoFechaYHora(hito.ocurrido_en, zona)
);

function LineaDeTiempo({ hitos, etapas, zona }: { hitos: HitoDelSeguimiento[]; etapas: EtapaDelSeguimiento[]; zona: string | null }) {
  if (hitos.length === 0) {
    return <p data-testid="seguimiento-sin-hitos" className="text-meta text-ink-soft">Sin hitos registrados.</p>;
  }
  return (
    <ol aria-label="Línea de tiempo del caso" className="space-y-1">
      {agrupar(hitos, etapas).map((grupo) => (
        <li key={grupo.clave} data-testid="seguimiento-grupo">
          {grupo.texto && (
            <h4 data-testid="seguimiento-grupo-etapa"
              className="pl-[8.25rem] sm:pl-[11.375rem] mt-1.5 mb-1 text-micro font-bold text-marca-profundo">
              {grupo.texto}
            </h4>
          )}
          <ol>
            {grupo.hitos.map(({ hito, indice }) => {
              const primero = indice === 0;
              return (
                <li key={`${hito.ocurrido_en}-${indice}`} data-testid="seguimiento-hito"
                  className="grid grid-cols-[5.625rem_1.125rem_1fr] sm:grid-cols-[11rem_1.125rem_1fr] gap-3">
                  <time dateTime={hito.ocurrido_en} className="text-right text-meta text-ink-soft tabular-nums pt-0.5">
                    {fechaDelHito(hito, zona)}
                  </time>
                  <span aria-hidden="true" className="relative">
                    <span className="absolute left-2 top-0 bottom-0 w-0.5 bg-hairline" />
                    <span className="absolute left-[3px] top-1.5 w-3 h-3 rounded-full bg-white border-2 border-marca" />
                  </span>
                  <div className="pb-3.5 min-w-0">
                    <p className="text-compacto font-semibold text-ink">{hito.texto}</p>
                    <p className="text-meta text-ink-soft">
                      {hito.actor ?? 'Sin responsable registrado'}
                      {' · '}
                      {hito.texto_desde_el_anterior || (primero ? 'punto de partida' : '')}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </li>
      ))}
    </ol>
  );
}

export default function SeguimientoDelCaso({ casoId, numero, pais, onCerrar }: PropsDeSeguimientoDelCaso) {
  const zona = useZonaDelPais()(pais);
  const [seguimiento, setSeguimiento] = useState<DatosDelSeguimiento | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const enCurso = useRef<AbortController | null>(null);

  const cargar = useCallback(async () => {
    enCurso.current?.abort();
    const control = new AbortController();
    enCurso.current = control;
    setCargando(true);
    setFallo(null);
    try {
      const respuesta = await consultarSeguimiento(casoId, { senal: control.signal });
      if (!control.signal.aborted) setSeguimiento(respuesta);
    } catch (error) {
      if (control.signal.aborted || (error instanceof Error && error.name === 'AbortError')) return;
      setSeguimiento(null);
      setFallo(aFallo(error));
    } finally {
      if (!control.signal.aborted) setCargando(false);
    }
  }, [casoId]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => () => enCurso.current?.abort(), []);

  const numeroVisible = seguimiento?.numero ?? numero;
  const titulo = numeroVisible ? `Seguimiento de ${numeroVisible}` : 'Seguimiento del caso';
  // 403 (fuera del ámbito) y 404 no se arreglan reintentando: se dice el texto del servidor y ya.
  const sinAcceso = fallo?.estado === 403 || fallo?.estado === 404;

  let cuerpo: ReactNode;
  if (cargando) {
    cuerpo = <p role="status" data-testid="seguimiento-cargando" className="text-meta text-ink-soft">Cargando el seguimiento…</p>;
  } else if (fallo && sinAcceso) {
    cuerpo = <Alert tone="warning" data-testid="seguimiento-sin-acceso">{fallo.message}</Alert>;
  } else if (fallo) {
    cuerpo = (
      <div className="space-y-3">
        <Alert tone="danger" data-testid="seguimiento-error" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>
        <Button variant="secondary" size="sm" icon={RefreshCw} onClick={cargar} data-testid="seguimiento-reintentar">
          Reintentar
        </Button>
      </div>
    );
  } else if (seguimiento) {
    const subtitulo = [seguimiento.texto_del_estado, seguimiento.responsable_actual].filter(Boolean).join(' · ');
    cuerpo = (
      <div className="space-y-5">
        <p data-testid="seguimiento-subtitulo" className="text-meta text-ink-soft -mt-2">{subtitulo}</p>
        <Flujo etapas={seguimiento.etapas} zona={zona} />
        <Resumen seguimiento={seguimiento} />
        <LineaDeTiempo hitos={seguimiento.hitos} etapas={seguimiento.etapas} zona={zona} />
      </div>
    );
  }

  return (
    <Modal open onClose={onCerrar} ancho="trabajo" icon={History} title={titulo} data-testid="seguimiento-modal"
      footer={<Button variant="secondary" onClick={onCerrar} data-testid="seguimiento-cerrar">Cerrar</Button>}>
      {cuerpo}
    </Modal>
  );
}
